import { useState, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import * as contractsApi from '../../api/contracts'
import * as attachmentsApi from '../../api/attachments'
import { useUIStore } from '../../stores/uiStore'
import Button from '../common/Button'
import SigningLinkPreview from './SigningLinkPreview'
import { XMarkIcon, PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/outline'

/**
 * Modal for sending signing links to trainers
 * @param {object} props
 * @param {object} props.contract - Contract object
 * @param {array} props.trainers - Array of linked trainers
 * @param {boolean} props.isOpen - Modal visibility
 * @param {function} props.onClose - Close handler
 * @param {boolean} props.isReminder - Whether this is a reminder (vs initial send)
 */
export default function SendSigningLinkModal({ 
  contract, 
  trainers = [], 
  isOpen, 
  onClose,
  isReminder = false 
}) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [selectedTrainers, setSelectedTrainers] = useState([])
  const [selectedAttachments, setSelectedAttachments] = useState([])

  // Fetch attachments for this contract
  const { data: attachments = [] } = useQuery({
    queryKey: ['contract', contract?.id, 'attachments'],
    queryFn: () => attachmentsApi.getAttachments(contract.id),
    enabled: !!contract?.id && isOpen
  })

  // Optional attachments are unchecked by default (no auto-selection)
  // Users must manually select which optional files to include

  // Auto-select single trainer
  useEffect(() => {
    if (trainers.length === 1 && selectedTrainers.length === 0) {
      setSelectedTrainers([trainers[0].id])
    }
  }, [trainers, selectedTrainers.length])

  const sendLinkMutation = useMutation({
    mutationFn: (data) => {
      if (isReminder) {
        return contractsApi.remindSigningLink(contract.id, data)
      }
      return contractsApi.sendSigningLink(contract.id, data)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['contract', contract.id])
      queryClient.invalidateQueries(['recent-activity'])
      showToast(
        data.message || `Signing link ${isReminder ? 'reminder' : ''} sent successfully!`,
        'success'
      )
      onClose()
      resetForm()
    },
    onError: (error) => {
      showToast(error.message || 'Failed to send signing link', 'error')
    },
  })

  const resetForm = () => {
    setSelectedTrainers([])
    setSelectedAttachments([])
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const data = {}
    
    // Only include trainer_ids if specific trainers are selected
    if (selectedTrainers.length > 0) {
      data.trainer_ids = selectedTrainers
    }
    
    // Include selected attachment IDs (optional attachments only, main contract is always included)
    if (selectedAttachments.length > 0) {
      data.attachment_ids = selectedAttachments
    }
    
    sendLinkMutation.mutate(data)
  }

  const toggleTrainer = (trainerId) => {
    setSelectedTrainers(prev => {
      if (prev.includes(trainerId)) {
        return prev.filter(id => id !== trainerId)
      }
      return [...prev, trainerId]
    })
  }

  const selectAll = () => {
    setSelectedTrainers(trainers.map(t => t.id))
  }

  const deselectAll = () => {
    setSelectedTrainers([])
  }

  const toggleAttachment = (attachmentId) => {
    setSelectedAttachments(prev => {
      if (prev.includes(attachmentId)) {
        return prev.filter(id => id !== attachmentId)
      }
      return [...prev, attachmentId]
    })
  }

  const selectAllAttachments = () => {
    setSelectedAttachments(attachments.map(att => att.id))
  }

  const deselectAllAttachments = () => {
    setSelectedAttachments([])
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isReminder ? 'Send Reminder' : 'Send Signing Link'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {contract?.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Trainer Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Select Trainers
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={deselectAll}
                  className="text-xs text-gray-600 hover:text-gray-700"
                >
                  Deselect All
                </button>
              </div>
            </div>
            
            {trainers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                No trainers linked to this contract
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {trainers.map((trainer) => (
                  <label
                    key={trainer.id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTrainers.includes(trainer.id)}
                      onChange={() => toggleTrainer(trainer.id)}
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{trainer.name}</p>
                      <p className="text-xs text-gray-500">{trainer.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {selectedTrainers.length === 0 
                ? 'All linked trainers will receive the email' 
                : `${selectedTrainers.length} trainer(s) selected`}
            </p>
          </div>

          {/* Optional Attachments Selection */}
          {attachments && attachments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <PaperClipIcon className="h-4 w-4 inline mr-1" />
                  Optional Attachments
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllAttachments}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={deselectAllAttachments}
                    className="text-xs text-gray-600 hover:text-gray-700"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 p-2 bg-green-100 rounded">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Main Contract
                      <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded">
                        ALWAYS INCLUDED
                      </span>
                    </p>
                    <p className="text-xs text-gray-600">The primary contract document</p>
                  </div>
                </div>
                
                {attachments.map((attachment) => (
                  <label
                    key={attachment.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAttachments.includes(attachment.id)}
                      onChange={() => toggleAttachment(attachment.id)}
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{attachment.file_name}</p>
                      {attachment.description && (
                        <p className="text-xs text-gray-600">{attachment.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                Main contract is always included. {selectedAttachments.length} optional file{selectedAttachments.length !== 1 ? 's' : ''} selected.
              </p>
            </div>
          )}

          {/* Preview Section */}
          {selectedTrainers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preview
              </label>
              <SigningLinkPreview
                contractId={contract.id}
                trainerId={selectedTrainers[0]}
                isReminder={isReminder}
                selectedAttachmentIds={selectedAttachments}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={sendLinkMutation.isPending}
              disabled={trainers.length === 0}
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              {isReminder ? 'Send Reminder' : 'Send Link'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}